import { object, string } from 'yup';

import { emailYupValidation } from '../../utils/validator';
import {
    CreateExternalUserParams,
    ExternalUserListItem,
    MFAType,
    UpdateExternalUserParams
} from './types';

export const getTupleValue = (
    entries: [string, string | boolean | null][] | null,
    key: string
): string | boolean | null | undefined => {
    return entries?.find(([k]) => k === key)?.[1];
};

export interface UserFormDefaultValues {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    role: string;
    bio: string;
    specialty: string;
    image: string;
}

export const validationSchema = object({
    first_name: string().trim().required('First name is required'),
    last_name: string().trim().required('Last name is required'),
    email: emailYupValidation,
    phone: string().trim().notRequired(),
    role: string().trim().notRequired(),
    bio: string().trim().notRequired(),
    specialty: string().trim().notRequired(),
    image: string().trim().notRequired()
});

export const buildUserFormDefaultValues = (user: ExternalUserListItem): UserFormDefaultValues => ({
    first_name: user.first_name ?? '',
    last_name: user.last_name ?? '',
    email: user.email ?? '',
    phone: user.phone ?? user.mobile ?? '',
    role: (getTupleValue(user.role, 'id') as string) ?? '',
    bio: user.bio ?? '',
    specialty: user.specialty ?? '',
    image: user.image_url ?? ''
});

interface BuildExternalUserPayloadOptions {
    externalUserId?: number | null;
    heroUserId?: number | null;
    originalImageUrl?: string | null;
    roleName?: string;
}

export const buildExternalUserPayload = (
    values: Record<string, string>,
    opts: BuildExternalUserPayloadOptions = {}
): UpdateExternalUserParams | CreateExternalUserParams => {
    const { externalUserId, heroUserId, originalImageUrl, roleName } = opts;

    const imageField: Partial<Pick<UpdateExternalUserParams, 'image'>> = {};
    if (values.image && values.image.startsWith('data:')) {
        imageField.image = values.image;
    } else if (externalUserId && !values.image && originalImageUrl) {
        imageField.image = null;
    }

    const base = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        bio: values.bio,
        specialty: values.specialty,
        ...(roleName && { role: roleName }),
        ...imageField
    };

    if (externalUserId) {
        return { id: externalUserId, ...base };
    }

    return {
        ...base,
        is_active: true,
        ...(heroUserId && { hero_user_id: heroUserId })
    };
};

export const getMFALabel = (mfaType?: MFAType) => {
    const type: Record<string, string> = {
        SMS: 'SMS',
        VOICE: 'Voice call',
        TOTP: 'Auth app',
        null: 'Not Set',
        undefined: 'N/A'
    };

    return type[`${mfaType}`];
};
